/**
 * Spec 008 — heritage library fetch wrappers. Read-only, public (no auth, no CSRF). Same discriminated-union
 * contract as lib/finance.ts: these NEVER throw; each returns an explicit outcome the UI renders across the
 * four states (loading is the caller's pre-fetch state). The catalog distinguishes `unavailable` (archive
 * missing at runtime, SC-046) from an ordinary reach `error`; a document distinguishes `notfound` (bad slug,
 * SC-042) from `unavailable`.
 */

const TIMEOUT_MS = 10_000;

export type DocumentGroup = 'thematic' | 'primary';

export interface Provenance {
  sourceRepository: string;
  sourceCommit: string;
  commitDate: string;
  method: string;
  archivedOn: string;
  compiler: string;
}

export interface Licence {
  textTranslationCompilation: string;
  primarySourcesAndImages: string;
}

export interface FacsimileRef {
  title: string;
  filename: string;
  assetUrl: string;
  sizeLabel: string;
  sha256: string;
}

export interface CatalogEntry {
  slug: string;
  title: string;
  descriptor: string;
  group: DocumentGroup;
  orderKey: number;
}

export interface CatalogResponse {
  available: boolean;
  provenance: Provenance;
  licence: Licence;
  thematic: CatalogEntry[];
  primary: CatalogEntry[];
}

export interface DocumentView {
  slug: string;
  title: string;
  group: DocumentGroup;
  bodyMarkdown: string;
  facsimiles: FacsimileRef[];
  provenance: Provenance;
  licence: Licence;
}

export type CatalogResult =
  | { kind: 'ok'; data: CatalogResponse }
  | { kind: 'unavailable' }
  | { kind: 'error' };

export type DocumentResult =
  | { kind: 'ok'; data: DocumentView }
  | { kind: 'notfound' }
  | { kind: 'unavailable' }
  | { kind: 'error' };

async function get(path: string): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    return await fetch(path, {
      method: 'GET',
      credentials: 'include',
      headers: { accept: 'application/json' },
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

export async function getCatalog(): Promise<CatalogResult> {
  try {
    const res = await get('/api/library');
    if (res.status === 503) return { kind: 'unavailable' };
    if (!res.ok) return { kind: 'error' };
    const data = (await res.json()) as CatalogResponse;
    // Belt and suspenders: a 200 with available:false also means the archive is unavailable.
    if (!data || data.available === false) return { kind: 'unavailable' };
    return { kind: 'ok', data };
  } catch {
    return { kind: 'error' };
  }
}

export async function getDocument(slug: string): Promise<DocumentResult> {
  try {
    const res = await get(`/api/library/${encodeURIComponent(slug)}`);
    if (res.status === 404) return { kind: 'notfound' };
    if (res.status === 503) return { kind: 'unavailable' };
    if (!res.ok) return { kind: 'error' };
    const data = (await res.json()) as DocumentView;
    return { kind: 'ok', data };
  } catch {
    return { kind: 'error' };
  }
}

/** Human size for a facsimile — the backend already provides a README-stated label; passthrough helper. */
export function facsimileSize(ref: FacsimileRef): string {
  return ref.sizeLabel;
}
